/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import { Alert, Box, Card, Divider, TextField, Typography } from '@mui/material';
import { Permission, Resource } from '@pepperhq/auth-client';
import { stringify } from 'query-string';

import { useAppSelector } from 'store/hooks';
import { shopifyApi, IShopifyReconcileResult } from 'components/settings/Integrations/Shopify/shopifyApi';
import { ILocationScopedSettingsComponentProps } from 'components/settings/LocationScopedSettings';
import { SettingDisplay } from 'lib/SettingDisplay';
import { LoadingButton } from 'ui/buttons/LoadingButton';

const SHOPIFY_SCOPES = 'read_orders';

// Base64 encode the state for Shopify auth
const encodeStateForShopify = (obj: object): string =>
    btoa(JSON.stringify(obj)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export const ShopifySettingsCard: React.FC<ILocationScopedSettingsComponentProps> = ({
    settings,
    locationId
}) => {
    const { tenantId } = useAppSelector(state => state.auth.tenant);
    const { claims } = useAppSelector(state => state.auth);
    const canRefreshWebhooks = React.useMemo(
        () => claims.hasPermission(Resource.InternalSetting, Permission.write),
        [claims]
    );
    const isAuthorized = React.useMemo(() => !!settings?.shopify?.oauth2AccessToken, [settings]);
    const [loadingOAuth, setLoadingOAuth] = React.useState(false);
    const [shopInput, setShopInput] = React.useState('');
    const [shopError, setShopError] = React.useState<string | null>(null);
    const [refreshingWebhooks, setRefreshingWebhooks] = React.useState(false);
    const [webhookResult, setWebhookResult] = React.useState<IShopifyReconcileResult | null>(null);
    const [webhookError, setWebhookError] = React.useState<string | null>(null);

    const handleShopInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setShopInput(event.target.value.trim());
        setShopError(null);
    }, []);

    const handleRefreshWebhooksClicked = React.useCallback(async () => {
        setRefreshingWebhooks(true);
        setWebhookResult(null);
        setWebhookError(null);
        try {
            const response = await shopifyApi.refreshWebhooks(locationId);
            if (response.ok) {
                setWebhookResult(response.body);
            } else {
                setWebhookError(`Refresh failed (status ${response.statusCode})`);
            }
        } catch (err) {
            setWebhookError(err instanceof Error ? err.message : 'Refresh failed');
        } finally {
            setRefreshingWebhooks(false);
        }
    }, [locationId]);

    const handleConnectClicked = React.useCallback(() => {
        const shop = shopInput.toLowerCase();
        if (shop.startsWith('http')) {
            setShopError(
                `Shop domain should not be the full URL (e.g. https://example.myshopify.com),
                only the store name as shown in the admin dashboard (e.g. example.myshopify.com)`
            );
            return;
        }
        setLoadingOAuth(true);
        const baseRedirectUrl = `${window.location.origin}${window.location.pathname}`;
        const query = {
            client_id: import.meta.env.SHOPIFY_CLIENT_ID,
            scope: SHOPIFY_SCOPES,
            redirect_uri: import.meta.env.SHOPIFY_REDIRECT_URI,
            state: encodeStateForShopify({
                tenantId,
                locationId,
                // Maybe make changes so we can redirect to having the correct location selected?
                successRedirectUrl: `${baseRedirectUrl}?oauthResult=success`,
                errorRedirectUrl: `${baseRedirectUrl}?oauthResult=error`
            })
        };
        window.location.href = `https://${shop}/admin/oauth/authorize?${stringify(query)}`;
    }, [locationId, shopInput, tenantId]);

    return (
        <Card sx={{ marginBottom: 2 }}>
            <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                padding={1}
                paddingLeft={2}
                minHeight={5.25}
            >
                <Typography variant="h6">Shopify</Typography>
            </Box>
            <Divider />
            <Box padding={2}>
                {isAuthorized ? (
                    <>
                        <SettingDisplay label="Shop">
                            <Typography variant="body1">{settings?.shopify?.shop}</Typography>
                        </SettingDisplay>
                        <SettingDisplay label="Scopes">
                            <Typography variant="body1">{settings?.shopify?.scopes}</Typography>
                        </SettingDisplay>
                        {canRefreshWebhooks && (
                            <SettingDisplay
                                label="Webhooks"
                                description="Re-register the Shopify webhooks for this location."
                            >
                                <Box display="flex" flexDirection="column" gap={1} alignItems="flex-start">
                                    <LoadingButton
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleRefreshWebhooksClicked}
                                        loading={refreshingWebhooks}
                                    >
                                        Refresh webhooks
                                    </LoadingButton>
                                    {webhookResult && (
                                        <Alert severity="success" variant="outlined" sx={{ width: '100%' }}>
                                            Reconciled — created {webhookResult.created.length}, updated{' '}
                                            {webhookResult.updated.length}, deleted{' '}
                                            {webhookResult.deleted.length}.
                                        </Alert>
                                    )}
                                    {webhookError && (
                                        <Alert severity="error" variant="outlined" sx={{ width: '100%' }}>
                                            {webhookError}
                                        </Alert>
                                    )}
                                </Box>
                            </SettingDisplay>
                        )}
                        <SettingDisplay label="Connection">
                            <Typography variant="body2" color="text.secondary">
                                To disconnect, uninstall the Pepper app from your Shopify store&rsquo;s admin
                                panel. Shopify will notify us and we will clear this connection automatically.
                            </Typography>
                        </SettingDisplay>
                    </>
                ) : (
                    <>
                        <SettingDisplay
                            label="Shop Domain"
                            description={`Your Shopify store name, not the full URL, e.g. example.myshopify.com`}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                value={shopInput}
                                onChange={handleShopInputChange}
                                placeholder={`example.myshopify.com`}
                                error={!!shopError}
                                helperText={shopError ?? undefined}
                            />
                        </SettingDisplay>
                        <SettingDisplay label="Connection">
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                onClick={handleConnectClicked}
                                loading={loadingOAuth}
                                disabled={!shopInput}
                            >
                                Connect Shopify
                            </LoadingButton>
                        </SettingDisplay>
                    </>
                )}
            </Box>
        </Card>
    );
};
